import React, { useMemo, createContext, useContext, useState, useCallback, useEffect } from 'react'
import zionDebug from '@fs/zion-debug'

function editMetadataIsOpen() {
  return 'editMetadataOpen'
}

// We had to use this file to avoid cyclical dependencies
export const OverlayContext = createContext(null)
const debug = zionDebug('siv:OverlayContext')

export default function OverlayContextProvider({
  infoSheetOverlay,
  editIndexOverlay,
  viewIndexOverlay,
  addPersonGenericOverlay,
  addPersonPage2GenericOverlay,
  dragNamesOverlay,
  removePersonOverlay,
  editSharedHighlightPage1Overlay,
  editSharedHighlightPage2Overlay,
  editHighlightsOverlay,
  editSharedFieldPage1Overlay,
  editSharedFieldPage2Overlay,
  editDanglingFieldOverlay,
  editOtherFieldsOverlay,
  editRelationshipsOverlay,
  editAgeOverlay,
  editEventsOverlay,
  editEssentialInfoOverlay,
  eventPanelOverlay,
  linkExistingEventOverlay,
  sharedEventOverlay,
  highlightsPanelOldOverlay,
  highlightsPanelOverlay,
  addNamesOverlay,
  attachToTreeOverlay, // Lightyear overlay
  editMetadataOverlay, // Lightyear overlay
  viewAnotherItemOverlay, // Lighyear overlay
  finishedCorrectingMetadataOverlay, // Lightyear overlay
  groupHistoryOverlay, // Lightyear overlay
  testOverlay,
  editHouseholdOverlay,
  children,
}) {
  const [panelStack, setPanelStack] = useState([])
  const [subscribers, setSubscribers] = useState([])
  const [overlays, setOverlays] = useState([])

  useEffect(() => {
    let isMounted = true

    if (isMounted) {
      setOverlays([
        { overlay: infoSheetOverlay, id: 'mainInfosheet' },
        { overlay: editIndexOverlay, id: 'editIndex' },
        { overlay: viewIndexOverlay, id: 'viewIndex' },
        { overlay: addPersonGenericOverlay, id: 'addPersonPage1Generic' },
        { overlay: addPersonPage2GenericOverlay, id: 'addPersonPage2Generic' },
        { overlay: dragNamesOverlay, id: 'dragNames' },
        { overlay: removePersonOverlay, id: 'removePerson' },
        { overlay: editSharedHighlightPage1Overlay, id: 'editSharedHighlightPage1' },
        { overlay: editSharedHighlightPage2Overlay, id: 'editSharedHighlightPage2' },
        { overlay: editHighlightsOverlay, id: 'editHighlights' },
        { overlay: editSharedFieldPage1Overlay, id: 'editSharedFieldPage1' },
        { overlay: editSharedFieldPage2Overlay, id: 'editSharedFieldPage2' },
        { overlay: editDanglingFieldOverlay, id: 'editDanglingField' },
        { overlay: editOtherFieldsOverlay, id: 'editOtherFields' },
        { overlay: editRelationshipsOverlay, id: 'editRelationships' },
        { overlay: editAgeOverlay, id: 'editAge' },
        { overlay: editEventsOverlay, id: 'editEvents' },
        { overlay: editEssentialInfoOverlay, id: 'editEssentialInfo' },
        { overlay: eventPanelOverlay, id: 'eventPanel' },
        { overlay: linkExistingEventOverlay, id: 'linkExistingEvent' },
        { overlay: sharedEventOverlay, id: 'sharedEvent' },
        { overlay: highlightsPanelOldOverlay, id: 'highlightsPanelOld' },
        { overlay: highlightsPanelOverlay, id: 'highlightsPanel' },
        { overlay: addNamesOverlay, id: 'addNamesOverlay' },
        { overlay: attachToTreeOverlay, id: 'attachToTreeOverlay' },
        { overlay: editMetadataOverlay, id: 'editMetadataOverlay' },
        { overlay: groupHistoryOverlay, id: 'groupHistoryOverlay' },
        { overlay: testOverlay, id: 'testOverlay' },
        { overlay: editHouseholdOverlay, id: 'editHouseholdPanel' },
      ])
    }

    return () => {
      isMounted = false // Marca como desmontado
    }
    // this only needs to be done once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If you don't find the overlay, something else is wrong.  Probably a mispelling in the id
  const removeFromStack = useCallback((overlayId) => {
    setPanelStack((prev) => prev?.filter((i) => i !== overlayId) || [])
  }, [])

  const subscribe = useCallback(
    (sub) => {
      setSubscribers((prev) => [...prev, sub])
    },
    [setSubscribers]
  )

  const unsubscribe = useCallback(
    (sub) => {
      setSubscribers((prev) => {
        const newSubs = [...prev]
        const index = prev.indexOf(sub)
        newSubs.splice(index, 1)
        return newSubs
      })
    },
    [setSubscribers]
  )

  // If you don't find the overlay, something else is wrong.  Probably a mispelling in the id
  const openOverlay = useCallback(
    (overlayId) => {
      removeFromStack(overlayId)
      setPanelStack((prev) => {
        return [...prev, overlayId]
      })
      const found = overlays.find((o) => o.id === overlayId)
      if (found) {
        found.overlay.open()
        if (overlayId === 'editMetadataOverlay') subscribe(editMetadataIsOpen)
      }
    },
    [overlays, removeFromStack, subscribe]
  )

  // If you don't find the overlay, something else is wrong.  Probably a mispelling in the id
  const closeOverlay = useCallback(
    (overlayId) => {
      removeFromStack(overlayId)
      const found = overlays.find((o) => o.id === overlayId)
      if (found) {
        found.overlay.close()
        if (overlayId === 'editMetadataOverlay') unsubscribe(editMetadataIsOpen)
      }
    },
    [overlays, removeFromStack, unsubscribe]
  )

  // This is a read only panel.  No need to handle close in the panel itself
  if (viewIndexOverlay) viewIndexOverlay.onClose = () => removeFromStack('viewIndex')

  const closeAll = useCallback(() => {
    overlays.forEach((o) => {
      if (o.id !== 'mainInfosheet' && o?.overlay) {
        o.overlay.close()
        removeFromStack(o.id)
      }
    })
  }, [overlays, removeFromStack])

  const getOverlay = useCallback(
    (id) => {
      return overlays?.find((o) => o.id === id)?.overlay
    },
    [overlays]
  )

  // It didn't work when I looped through the overlays array checking isOpen so I had to check on each object.  Bummer :(
  const allPanelsClosed = useCallback(() => {
    return Boolean(
      !editIndexOverlay?.isOpen &&
      !viewIndexOverlay?.isOpen &&
      !addPersonGenericOverlay?.isOpen &&
      !addPersonPage2GenericOverlay?.isOpen &&
      !dragNamesOverlay?.isOpen &&
      !removePersonOverlay?.isOpen &&
      !editSharedHighlightPage1Overlay?.isOpen &&
      !editSharedHighlightPage2Overlay?.isOpen &&
      !editHighlightsOverlay?.isOpen &&
      !editSharedFieldPage1Overlay?.isOpen &&
      !editSharedFieldPage2Overlay?.isOpen &&
      !editDanglingFieldOverlay?.isOpen &&
      !editOtherFieldsOverlay?.isOpen &&
      !editRelationshipsOverlay?.isOpen &&
      !editMetadataOverlay?.isOpen &&
      !editAgeOverlay?.isOpen &&
      !editEventsOverlay?.isOpen &&
      !editEssentialInfoOverlay?.isOpen &&
      !eventPanelOverlay?.isOpen &&
      !linkExistingEventOverlay?.isOpen &&
      !sharedEventOverlay?.isOpen &&
      !groupHistoryOverlay?.isOpen &&
      !highlightsPanelOldOverlay?.isOpen &&
      !highlightsPanelOverlay?.isOpen &&
      !addNamesOverlay?.isOpen &&
      !attachToTreeOverlay?.isOpen &&
      !testOverlay?.isOpen &&
      !editHouseholdOverlay?.isOpen
    )
  }, [
    editIndexOverlay?.isOpen,
    viewIndexOverlay?.isOpen,
    addPersonGenericOverlay?.isOpen,
    addPersonPage2GenericOverlay?.isOpen,
    dragNamesOverlay?.isOpen,
    removePersonOverlay?.isOpen,
    editSharedHighlightPage1Overlay?.isOpen,
    editSharedHighlightPage2Overlay?.isOpen,
    editHighlightsOverlay?.isOpen,
    editSharedFieldPage1Overlay?.isOpen,
    editSharedFieldPage2Overlay?.isOpen,
    editDanglingFieldOverlay?.isOpen,
    editOtherFieldsOverlay?.isOpen,
    editRelationshipsOverlay?.isOpen,
    editMetadataOverlay?.isOpen, // Include the missing dependency here
    editAgeOverlay?.isOpen,
    editEventsOverlay?.isOpen,
    editEssentialInfoOverlay?.isOpen,
    eventPanelOverlay?.isOpen,
    linkExistingEventOverlay?.isOpen,
    sharedEventOverlay?.isOpen,
    highlightsPanelOldOverlay?.isOpen,
    highlightsPanelOverlay?.isOpen,
    addNamesOverlay?.isOpen,
    attachToTreeOverlay?.isOpen,
    groupHistoryOverlay?.isOpen,
    testOverlay?.isOpen,
    editHouseholdOverlay?.isOpen,
  ])

  const topMostPanel = useCallback(() => {
    return panelStack.slice(-1)?.[0]
  }, [panelStack])

  // We want to know if the panel is open basically
  const inPanelStack = useCallback(
    (overlayId) => {
      return panelStack.find((i) => i === overlayId)
    },
    [panelStack]
  )

  /**
   * The user is switching to a different image.  We need to see if any forms are dirty and save first
   * @type {(function((function())=))|*} optional pendingAction param is a callback that will be called after the image switch is confirmed
   */
  const switchImageCheck = useCallback(
    (pendingAction) => {
      let ok = true
      let editPanelOpen = false
      subscribers.forEach((sub) => {
        // Forward pendingAction callback to each subscriber
        // for handling deferred operations after user confirmation
        const okyDoky = sub(topMostPanel(), pendingAction)
        if (okyDoky === 'editMetadataOpen') editPanelOpen = true
        if (!okyDoky) ok = false
      })
      if (ok && !editPanelOpen) {
        closeAll()
        if (panelStack.includes('editHouseholdPanel')) {
          // This addresses an issue with users being stranded after switching images.
          // This won't be necessary if EditHousehold becomes a child element of the main infosheet instead of being its own infosheet.
          openOverlay('mainInfosheet')
          setPanelStack(['mainInfosheet'])
        } else {
          setPanelStack([])
        }
        return true
      }
      return ok || editPanelOpen
    },
    [closeAll, subscribers, topMostPanel, openOverlay, panelStack]
  )

  debug({
    panelStack,
    subscribers,
    topMostPanel: topMostPanel(),
  })

  const overlayContextValue = useMemo(
    () => ({
      infoSheetOverlay,
      editIndexOverlay,
      viewIndexOverlay,
      addPersonGenericOverlay,
      addPersonPage2GenericOverlay,
      dragNamesOverlay,
      removePersonOverlay,
      editSharedHighlightPage1Overlay,
      editSharedHighlightPage2Overlay,
      editHighlightsOverlay,
      editSharedFieldPage1Overlay,
      editSharedFieldPage2Overlay,
      editDanglingFieldOverlay,
      editOtherFieldsOverlay,
      editRelationshipsOverlay,
      editAgeOverlay,
      editEventsOverlay,
      editEssentialInfoOverlay,
      eventPanelOverlay,
      linkExistingEventOverlay,
      sharedEventOverlay,
      highlightsPanelOverlay,
      highlightsPanelOldOverlay,
      addNamesOverlay,
      attachToTreeOverlay,
      editMetadataOverlay,
      viewAnotherItemOverlay,
      finishedCorrectingMetadataOverlay,
      groupHistoryOverlay,
      testOverlay,
      editHouseholdOverlay,
      topMostPanel,
      closeAll,
      allPanelsClosed,
      switchImageCheck,
      subscribe,
      unsubscribe,
      openOverlay,
      closeOverlay,
      getOverlay,
      inPanelStack,
    }),
    [
      infoSheetOverlay,
      editIndexOverlay,
      viewIndexOverlay,
      addPersonGenericOverlay,
      addPersonPage2GenericOverlay,
      dragNamesOverlay,
      removePersonOverlay,
      editSharedHighlightPage1Overlay,
      editSharedHighlightPage2Overlay,
      editHighlightsOverlay,
      editSharedFieldPage1Overlay,
      editSharedFieldPage2Overlay,
      editDanglingFieldOverlay,
      editOtherFieldsOverlay,
      editRelationshipsOverlay,
      editAgeOverlay,
      editEventsOverlay,
      editEssentialInfoOverlay,
      eventPanelOverlay,
      linkExistingEventOverlay,
      sharedEventOverlay,
      highlightsPanelOverlay,
      highlightsPanelOldOverlay,
      addNamesOverlay,
      attachToTreeOverlay,
      editMetadataOverlay,
      viewAnotherItemOverlay,
      finishedCorrectingMetadataOverlay,
      groupHistoryOverlay,
      testOverlay,
      editHouseholdOverlay,
      topMostPanel,
      closeAll,
      allPanelsClosed,
      switchImageCheck,
      subscribe,
      unsubscribe,
      openOverlay,
      closeOverlay,
      getOverlay,
      inPanelStack,
    ]
  )

  return <OverlayContext.Provider value={overlayContextValue}>{children}</OverlayContext.Provider>
}

export const useOverlayContext = () => {
  const context = useContext(OverlayContext)
  if (!context) {
    throw new Error(
      'useOverlayContext must be used within a OverlayContextProvider. If writing a test, use <StateProvider> from testUtils.js'
    )
  }
  return context
}
